import React, { useEffect, useState } from 'react';
import { BellOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Badge, Button, Dropdown, Typography, notification as antNotification } from 'antd';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import api from '~/services/api';
import { initSocket } from '~/services/socketService';

const { Text } = Typography;

const Notifications = () => {
  const profile = useSelector((state) => state.user.profile);
  const [notifications, setNotifications] = useState([]);

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error('Failed to mark as read', err);
    }
  };

  const handleDownloadReport = async (downloadUrl, notificationId) => {
    try {
      const response = await api.get(downloadUrl, {
        responseType: 'blob',
      });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `Inventory_Stock_Report_${moment().format('YYYY-MM-DD')}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');

      // Mark notification as read after download
      markAsRead(notificationId);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report. The file may have expired.');
    }
  };

  useEffect(() => {
    if (!profile?.id) return;

    // Fetch unread notifications
    (async () => {
      try {
        const res = await api.get('/notifications');
        setNotifications(res.data.filter((n) => !n.read));
      } catch (err) {
        console.error('Failed to load notifications', err);
      }
    })();

    // Initialize shared socket connection
    initSocket(profile.id);

    // Listen for notifications from any tab
    const handler = (e) => {
      let notification = e.detail;

      // Parse context if it's a JSON string (from Socket.IO)
      if (notification.context && typeof notification.context === 'string') {
        try {
          notification = {
            ...notification,
            context: JSON.parse(notification.context),
          };
        } catch (err) {
          console.error('Failed to parse notification context:', err);
        }
      }

      // console.log('[Notifications] Processed notification:', notification);
      setNotifications((prev) => [notification, ...prev]);

      // Show visual notification popup for report notifications
      if (notification.type === 'report_ready') {
        antNotification.success({
          message: 'Report Ready',
          description: notification.message || 'Your Inventory Stock Report is ready to download',
          icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
          placement: 'topRight',
          duration: 6,
          btn: notification.context?.downloadUrl ? (
            <Button
              type="primary"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => {
                handleDownloadReport(notification.context.downloadUrl, notification.id);
                antNotification.destroy();
              }}
            >
              Download Now
            </Button>
          ) : null,
        });
      } else if (notification.type === 'report_failed') {
        antNotification.error({
          message: 'Report Generation Failed',
          description: notification.message || 'Failed to generate Inventory Stock Report',
          icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
          placement: 'topRight',
          duration: 6,
        });
      }
    };
    window.addEventListener('socket-notification', handler);

    return () => {
      window.removeEventListener('socket-notification', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id]);

  moment.updateLocale('en', {
    relativeTime: {
      future: 'in %s',
      past: '%s ago',
      s: 'a few seconds',
      ss: '%d seconds',
      m: '1 min',
      mm: '%d min',
      h: '1 hour',
      hh: '%d hours',
      d: '1 day',
      dd: '%d days',
      M: '1 month',
      MM: '%d months',
      y: '1 year',
      yy: '%d years',
    },
  });

  const dropdownItems =
    notifications.length > 0
      ? notifications.map((item) => {
          // Parse context if it's a string (defensive check)
          let itemContext = item.context;
          if (itemContext && typeof itemContext === 'string') {
            try {
              itemContext = JSON.parse(itemContext);
            } catch (err) {
              console.error('Failed to parse context for item:', item.id);
            }
          }

          const isReportReady = item.type === 'report_ready';
          const hasDownloadUrl = itemContext?.downloadUrl;

          return {
            key: item.id,
            label: (
              <div style={{ padding: '12px 16px', width: 320, borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text strong style={{ fontSize: 14 }}>
                    {item.type?.toUpperCase() || 'Notification'}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {moment(item.createdAt).fromNow()}
                  </Text>
                </div>
                <Text
                  style={{
                    fontSize: 13,
                    color: '#444',
                    lineHeight: 1.4,
                    display: 'block',
                    marginBottom: 8,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'break-word',
                  }}
                >
                  {itemContext?.link ? (
                    <Button
                      style={{ margin: 0, padding: 0, wordBreak: 'break-word', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}
                      type="link"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`/${itemContext.link}`}
                    >
                      {item.message}
                    </Button>
                  ) : (
                    item.message
                  )}
                </Text>
                {isReportReady && hasDownloadUrl && (
                  <>
                    {itemContext?.expirationDate && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                        Expires: {moment(itemContext.expirationDate).format('MMM DD, YYYY')}
                      </Text>
                    )}
                    <Button
                      type="primary"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadReport(itemContext.downloadUrl, item.id);
                      }}
                      style={{ marginBottom: 8, width: '100%' }}
                    >
                      Download Report
                    </Button>
                  </>
                )}
                <div style={{ textAlign: 'right' }}>
                  <Button
                    size="small"
                    type="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsRead(item.id);
                    }}
                    style={{ fontSize: 12, height: 22 }}
                  >
                    Mark as read
                  </Button>
                </div>
              </div>
            ),
          };
        })
      : [
          {
            key: 'empty',
            label: <div style={{ padding: 16, textAlign: 'center', color: 'rgba(0,0,0,0.45)' }}>No unread notifications</div>,
            disabled: true,
          },
        ];

  return (
    <Dropdown
      menu={{
        items: dropdownItems,
        style: { background: 'white', padding: '8px', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' },
      }}
      placement="bottomRight"
      trigger={['click']}
    >
      <Badge count={notifications.length} offset={[5, 0]}>
        <BellOutlined style={{ fontSize: '1.8em', color: 'white', cursor: 'pointer', marginRight: '-8px' }} />
      </Badge>
    </Dropdown>
  );
};

export default Notifications;
